import { CSSObject } from "styled-components";
import { ClaimHelper, StringHelper, TokenHelper } from "../utils/helpers";
import { permissions, roles } from "./claimSettings";
import { Constants } from "../utils/lib/constants";

export type ISectionMenuItem = {
    title?: string;
    icon?: string;

    hasTopSeparator?: boolean;
    hasBottomSeparator?: boolean;
    isDisplayed?: boolean;
    isDisabled?: boolean;

    overrideStyle?: CSSObject | undefined;

    requiredPermissions?: string[];

    items: IMenuItem[];
};

export type IMenuItem = {
    title: string;
    icon?: string;
    href: string;

    isDisplayed?: boolean;
    isDisabled?: boolean;

    hasAlert?: boolean;
    alertTitle?: string;

    overrideStyle?: CSSObject | undefined;
    overrideActiveStyle?: CSSObject | undefined;

    requiredPermissions?: string[];
};

export type ISideNavSettings = {
    mainSections: ISectionMenuItem[];
    footerSections: ISectionMenuItem[];
    mobileSections: ISectionMenuItem[];
};

const mainSections: ISectionMenuItem[] = [
    {
        title: "",
        hasTopSeparator: false,
        hasBottomSeparator: true,
        isDisplayed: true,

        items: [
            {
                title: "Community Management",
                icon: "bi bi-sliders",
                href: "/community",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageCommunitySafety,
                ],
            },
        ],
    },
    {
        title: "",
        hasTopSeparator: false,
        hasBottomSeparator: true,
        isDisplayed: true,
        items: [
            {
                title: "Service Request",
                icon: "bi bi-card-checklist",
                href: "/serviceRequest",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageServiceRequest,
                ],
            },
        ],
    },
    {
        title: "App Content",
        hasTopSeparator: false,
        hasBottomSeparator: true,
        isDisplayed: true,
        items: [
            {
                title: "Home",
                icon: "bi bi-collection",
                href: "/herenow/reference",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageResource,
                ],
            },
            {
                title: "Explore",
                icon: "bi bi-compass",
                href: "/herenow/explore",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageResource,
                ],
            },
            {
                title: "Nav Menu List",
                icon: "bi bi-link-45deg",
                href: "/herenow/navmenu",
                isDisplayed: false,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageQuicklinks,
                ],
            },
            {
                title: "Banner",
                icon: "bi bi-broadcast",
                href: "/herenow/banner",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageQuicklinks,
                ],
            },
        ],
    },

    {
        title: "App Features",
        hasTopSeparator: false,
        hasBottomSeparator: true,
        isDisplayed: true,

        items: [
            {
                title: "Community Resource",
                icon: "bi bi-building",
                href: "/herenow/communityresource",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageCommunity,
                ],
            },
            {
                title: "Nonprofit",
                icon: "bi bi-clipboard",
                href: "/herenow/nonprofit",
                isDisplayed: false,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageCommunity,
                ],
            },
            {
                title: "Community Development",
                icon: "bi bi-file-image",
                href: "/herenow/communitydevelopment",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageCommunityDevelopment,
                ],
            },
        ],
    },

    {
        title: "App Communication",
        hasTopSeparator: false,
        hasBottomSeparator: true,
        isDisplayed: true,

        items: [
            {
                title: "Push Notification",
                icon: "bi bi-app-indicator",
                href: "/herenow/push-notification",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManagePushNotifications,
                ],
            },
            {
                title: "Poll",
                icon: "bi bi-card-checklist",
                href: "/poll",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageQuestionnaire,
                ],
            },
            {
                title: "Surveys/Feedback",
                icon: "bi bi-card-checklist",
                href: "/feedback",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageQuestionnaire,
                ],
            },
        ],
    },
];

const footerSections: ISectionMenuItem[] = [
    {
        title: "Community Safety",
        hasTopSeparator: true,
        hasBottomSeparator: false,
        isDisplayed: true,
        overrideStyle: {
            // theme.palette.tab.main -- distinct from the brand accent (orange,
            // used for hover/interactive states) and from the danger/warning
            // status colors, so this reads as "a distinct feature section"
            // rather than borrowing meaning from an interactive or alert color.
            backgroundColor: "var(--color-tab)",
            color: "var(--color-white-solid)",
        },

        items: [
            {
                title: "Safety Perception",
                icon: "bi bi-card-checklist",
                href: "/survey",
                isDisplayed: true,
                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ManageCommunitySafety,
                ],
            },
        ],
    },
    {
        title: "",
        hasTopSeparator: true,
        hasBottomSeparator: false,
        isDisplayed: true,
        items: [
            {
                title: "Settings",
                icon: "bi bi-gear",
                href: "/adminsettings",
                isDisplayed: true,

                requiredPermissions: [
                    permissions.SystemFull,
                    permissions.ViewAdminSettingSection,
                ],
            },
        ],
    },
];

const mobileSections = (
    name?: string,
    hasNewNotification?: boolean,
): ISectionMenuItem[] => [
        {
            isDisplayed: true,
            hasTopSeparator: true,
            items: [
                {
                    title: "Notifications",
                    icon: "bi bi-bell-fill",
                    href: "/notifications",
                    hasAlert: hasNewNotification,
                    alertTitle: "Notifications",
                    isDisplayed: true,
                },
                {
                    title: StringHelper.whenNullOrEmpty(name, ""),
                    icon: "bi bi-person-square",
                    href: "/account",
                    isDisplayed: true,
                },
            ],
        },
    ];

function FilterSectionItems(
    items: ISectionMenuItem[],
    userClaims: any,
): ISectionMenuItem[] {
    return items.filter((section) => {
        if (section.isDisplayed === false) {
            return false;
        }

        const includeSection: boolean =
            (section.isDisplayed || section.isDisplayed === undefined) &&
            ClaimHelper.hasPermission(section.requiredPermissions, userClaims);
        if (includeSection) {
            section.items = FilterMenuItems(section.items, userClaims);
        }

        return includeSection && section.items?.length > 0;
    });
}

function FilterMenuItems(items: IMenuItem[], userClaims: any): IMenuItem[] {
    return items.filter(
        (item) =>
            ClaimHelper.hasPermission(item.requiredPermissions, userClaims) &&
            (item.isDisplayed || item.isDisplayed === undefined),
    );
}

export const GetSideNavSettings = (
    isAuthenticated?: boolean,
    name?: string,
    hasNewNotification?: boolean,
    communityId?: string | null,
): ISideNavSettings => {
    if (isAuthenticated) {
        const userClaims = TokenHelper.getClaims();
        const hasSelectedCommunity =
            !!communityId && communityId !== Constants.emptyGuid;
        return {
            mainSections: hasSelectedCommunity
                ? FilterSectionItems(mainSections, userClaims)
                : [],
            footerSections: FilterSectionItems(footerSections, userClaims),
            mobileSections: FilterSectionItems(
                mobileSections(name, hasNewNotification),
                userClaims,
            ),
        };
    }

    return {
        mainSections: [],
        footerSections: [],
        mobileSections: [],
    };
};

export const IsSideNavNotEmpty = (settings: ISideNavSettings): boolean => {
    return (
        settings.mainSections?.length +
        settings.footerSections?.length +
        settings.mobileSections?.length >
        0
    );
};

export const IsSideNavEmpty = (settings: ISideNavSettings): boolean => {
    return !IsSideNavNotEmpty(settings);
};